/**
 * Bridging between the viewer's two study payload shapes.
 *
 * The authenticated viewer opens on `GET /study/{id}/summary`, which carries one
 * `SeriesSummary` per series and NO instance rows; the share view (and the
 * non-viewer surfaces) still get the fat `GetStudyResponse` with every instance
 * inlined. Both become a {@link ViewerStudy}, so every viewer component sees one
 * type — the summary-only fields are optional and simply absent on a fat payload.
 *
 * The rule that keeps the two honest:
 *
 *   - `series.images` is "the instances loaded SO FAR". Index into it, iterate
 *     it, size a stack from it.
 *   - `seriesImageCount(series)` is "how many instances the series HAS". Display
 *     it, size a progress bar with it, decide whether a page is still missing.
 *
 * Reading `images.length` where the count was meant is the failure mode this
 * module exists to prevent: on a summary-loaded study it silently reads 0.
 */
/**
 * Project one `SeriesSummary` onto the viewer's series shape.
 *
 * `images` starts empty — `ensureSeriesInstances` fills it. The series-level
 * thumbnail is mapped INTO the existing `thumbnails` array (as its single
 * entry) rather than replacing it: every thumbnail consumer in the viewer
 * already reads `thumbnails[0]`, and keeping that shape means the blur-up
 * placeholder, the strip tile, the preview panel and the AI thumbnail collector
 * all keep working against either payload with no per-site branching.
 */
export function seriesSummaryToViewerSeries(summary) {
    return {
        id: summary.id,
        seriesUid: summary.seriesUid,
        seriesNumber: summary.seriesNumber,
        modality: summary.modality,
        seriesDescription: summary.seriesDescription,
        bodyPartExamined: summary.bodyPartExamined ?? undefined,
        images: [],
        // Either URL is an address, so either one is a thumbnail worth carrying: an
        // onsite object whose cloud upload has not landed reports no cloud URL and
        // only the node's LAN one (see lib/dicom/imageAvailability). Gating on the
        // cloud URL alone left those series with an empty array, and every consumer
        // that reads `thumbnails[0]` then fell through to its placeholder tile.
        thumbnails: summary.thumbnailStorageLocation || summary.thumbnailLocalStorageLocation
            ? [
                {
                    imageId: summary.thumbnailImageId ?? null,
                    storageLocation: summary.thumbnailStorageLocation,
                    localStorageLocation: summary.thumbnailLocalStorageLocation,
                },
            ]
            : [],
        imageCount: summary.imageCount,
        frameCount: summary.frameCount,
        isVideo: summary.isVideo,
        isMicroscopy: summary.isMicroscopy,
        firstImageStorageLocation: summary.firstImageStorageLocation,
        firstImageLocalStorageLocation: summary.firstImageLocalStorageLocation,
        firstImagePixelsPending: summary.firstImagePixelsPending,
        firstImageSimpleName: summary.firstImageSimpleName,
        frameRateFps: summary.frameRateFps,
        loopInstanceNumber: summary.loopInstanceNumber,
        // Not put into `images`: the row reaches the stack the way every page does,
        // through the instance loader's delivery. See `summaryFirstPage`.
        ...(summary.singleImage ? { singleImage: summary.singleImage } : {}),
    };
}
/**
 * Page 1 of a series, as the summary already carried it — or `undefined` when
 * it did not, and the page has to be asked for.
 *
 * The summary carries the whole row of a series that holds ONE instance whose
 * pixels have landed: the X-ray shape, where a study is dozens of one-image
 * series and asking for each one's page, one round trip apiece, was most of the
 * time it took to open. This is that row shaped exactly as the instances
 * endpoint would have answered for it. `bundles` is empty because the server
 * mints none for a series that small (its bundle floor): the pane fetches the
 * one object itself, and a bundle behind it would download the same bytes again.
 *
 * Gated on the count as well as the row, so a series that has grown past one
 * instance since the row was read is paged from the network like any other.
 */
export function summaryFirstPage(series) {
    const row = series.singleImage;
    if (!row || seriesImageCount(series) !== 1)
        return undefined;
    return {
        seriesId: series.id,
        page: 1,
        pageSize: 1,
        total: 1,
        hasMore: false,
        images: [row],
        bundles: [],
    };
}
/**
 * Project the summary study payload onto the shape the viewer holds in state.
 *
 * The study's structured reports ride along beside `series`, never inside it:
 * an SR is no image, so it must not reach a count, a hanging protocol, the
 * presentation memory or a drop target. The strip draws them as their own
 * tiles (components/viewer/SrStripTile). A server that predates the field
 * reads as "none".
 */
export function studySummaryToViewerStudy(summary) {
    return {
        ...summary,
        series: summary.series.map(seriesSummaryToViewerSeries),
        structuredReports: summary.structuredReports ?? [],
    };
}
/** Total instances in the series, whether or not they have been paged in yet. */
export function seriesImageCount(series) {
    if (!series)
        return 0;
    return series.imageCount ?? series.images.length;
}
/**
 * Total frames in the series (a single-frame instance counts as 1). Falls back
 * to summing the loaded instances when the server-side total is absent.
 */
export function seriesFrameCount(series) {
    if (!series)
        return 0;
    if (series.frameCount != null)
        return series.frameCount;
    return series.images.reduce((n, img) => n + (img.numberOfFrames ?? 1), 0);
}
/**
 * The first instance's `simpleName` — the fallback series label. Prefers the
 * hoisted summary field so a series labels correctly before its instances land.
 */
export function seriesFirstImageSimpleName(series) {
    if (!series)
        return null;
    return series.firstImageSimpleName ?? series.images[0]?.simpleName ?? null;
}
/**
 * The loop's DICOM instance number when the series is ONE cine loop that ingest
 * split out of a larger DICOM series, else null — what the clip label
 * ("Clip 17") and the clip's file name number.
 *
 * The server's `loopInstanceNumber` is the only source. Only the server knows
 * whether a series was really split into several rows (a lone multi-frame
 * object, like an enhanced CT, is one series and no clip), and a single series
 * cannot show that from here. A payload without the field (an older server, the
 * E2E seed) simply shows no clip label.
 */
export function seriesLoopInstanceNumber(series) {
    return series?.loopInstanceNumber ?? null;
}
/**
 * The first instance's object URLs, for the surfaces that play/download the
 * series representative directly (video viewport, poster decode) rather than
 * going through the Cornerstone stack.
 *
 * Prefers a genuinely loaded first instance — it carries the frame rate and the
 * instance ordering — and falls back to the summary's hoisted URLs so a video
 * series plays without an instances round trip.
 */
export function seriesFirstImage(series) {
    if (!series)
        return undefined;
    const loaded = series.images.length
        ? series.images.toSorted((a, b) => (a.instanceNumber ?? 0) - (b.instanceNumber ?? 0))[0]
        : undefined;
    if (loaded)
        return loaded;
    // Either hoisted URL is an address. An onsite first object whose cloud upload
    // has not landed reports only the node's LAN URL, and that object is genuinely
    // fetchable (see lib/dicom/imageAvailability) — gating on the cloud URL alone
    // hid it from the video viewport and the poster decode entirely.
    if (!series.firstImageStorageLocation && !series.firstImageLocalStorageLocation) {
        return undefined;
    }
    return {
        storageLocation: series.firstImageStorageLocation ?? null,
        localStorageLocation: series.firstImageLocalStorageLocation,
        frameRateFps: series.frameRateFps,
    };
}
/**
 * Whether every instance of the series is already in `images`.
 *
 * This is what keeps the share view off the instances endpoint entirely: its fat
 * payload has no `imageCount`, so the count falls back to `images.length` and
 * the series is loaded by definition. A summary-loaded series with a real
 * `imageCount` only reads as loaded once its pages have actually arrived.
 */
export function isSeriesFullyLoaded(series) {
    if (!series)
        return false;
    return series.images.length >= seriesImageCount(series);
}
/**
 * The series the viewer pages in ON THE READER'S BEHALF: every series bound to a
 * pane, plus the strip selection.
 *
 * This is the eager pass' `wanted` set (components/viewer/ViewerShell), and it
 * lives here because the series strip has to answer the same question — a tile
 * may only claim to be loading for a series the shell actually asked for, and
 * two copies of this rule drifting apart is a tile that spins for a series
 * nothing is fetching — or, worse, stays silent for one that is. A strip click
 * binds the active pane (ViewerContext's SELECT_SERIES), so the selection is
 * almost always one of the panes' series already; it stays in the set for the
 * moments it is not (a study swap, before the panes are filled).
 *
 * Look-ahead's own speculative window is deliberately NOT in here. That tier
 * warms unopened series in the background whether or not anyone asked, and
 * badging those tiles would report work the reader never requested.
 */
export function focusedSeriesIds(viewports, selectedSeriesId) {
    const focused = new Set();
    viewports.forEach((vp) => {
        if (vp.seriesId)
            focused.add(vp.seriesId);
    });
    if (selectedSeriesId)
        focused.add(selectedSeriesId);
    return focused;
}
